import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SiweMessage } from "siwe";
import {
  useAccount,
  useChainId,
  useDisconnect,
  useSignMessage,
  useSwitchChain
} from "wagmi";
import { api } from "./api";
import { ensureConfiguredBscNetwork } from "./chain";

type NonceResponse = {
  nonce: string;
  domain: string;
  uri: string;
  chainId: number;
  expiresInSeconds: number;
};

export function useWalletSession() {
  const account = useAccount();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { signMessageAsync } = useSignMessage();
  const { disconnectAsync } = useDisconnect();
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: ["wallet-session", account.address],
    queryFn: () => api<{ authenticated: boolean; address: string }>("/auth/session"),
    retry: false,
    enabled: account.isConnected
  });
  const login = useMutation({
    mutationFn: async () => {
      if (!account.address) throw new Error("请先连接钱包");
      await ensureConfiguredBscNetwork(account.connector, chainId, switchChainAsync);
      const nonce = await api<NonceResponse>("/auth/nonce");
      const issuedAt = new Date();
      const message = new SiweMessage({
        domain: nonce.domain,
        address: account.address,
        statement: "Sign in to OnChainSage to view and manage your node assets.",
        uri: nonce.uri,
        version: "1",
        chainId: nonce.chainId,
        nonce: nonce.nonce,
        issuedAt: issuedAt.toISOString(),
        expirationTime: new Date(issuedAt.getTime() + nonce.expiresInSeconds * 1000).toISOString()
      }).prepareMessage();
      const signature = await signMessageAsync({ message });
      return api<{ address: string }>("/auth/verify", {
        method: "POST",
        body: JSON.stringify({ message, signature })
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["wallet-session"] });
      await queryClient.invalidateQueries({ queryKey: ["assets"] });
    }
  });
  const logout = useMutation({
    mutationFn: () => api<{ loggedOut: boolean }>("/auth/logout", { method: "POST" }),
    onSettled: async () => {
      queryClient.clear();
      await disconnectAsync();
    }
  });
  return {
    account,
    session,
    login,
    logout,
    authenticated: Boolean(
      session.data?.authenticated
      && account.address
      && session.data.address.toLowerCase() === account.address.toLowerCase()
    )
  };
}
